import React from "react";
import { Table, Spinner, Badge, Alert, Form, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { usePaytables } from "../util/db";
import {
  useDivisionPayoutCalculations,
  calculatePayoutSummary,
} from "../util/payoutCalculations";

function DivisionPayoutTab({
  eventId,
  eventDivisionsId,
  divisionName,
  divisionData,
  scoringOrderGolf,
  throwScoringModeId,
  payoutPercentage,
  selectedPaytableId,
  onUpdateSettings,
}) {
  // Create division settings object for the hook
  const divisionSettings = {
    payoutPercentage,
    selectedPaytableId,
  };

  // Use the shared payout calculation hook
  const {
    data: payoutResult,
    status: payoutStatus,
    error: payoutError,
  } = useDivisionPayoutCalculations(
    eventId,
    divisionData,
    divisionSettings,
    scoringOrderGolf
  );

  // Fetch available paytables for dropdown
  const { data: availablePaytables, status: paytablesStatus } = usePaytables();

  // Extract data from the payout result
  const players = payoutResult?.players || [];
  const playersWithRanks = payoutResult?.playersWithRanks || [];
  // const totalPlayers = payoutResult?.totalPlayers || 0;
  const placesPaid = payoutResult?.placesPaid || 0;
  const prizePool = payoutResult?.prizePool || 0;
  const allPayouts = payoutResult?.allPayouts || {};
  const paytableData = payoutResult?.paytableData || [];
  const getPayoutInfo =
    payoutResult?.getPayoutInfo ||
    (() => ({ amount: 0, percentage: 0, status: "No" }));

  // Only show loading spinner for initial load or critical errors, not for setting changes
  if (payoutStatus === "loading") {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <div className="mt-2">Loading {divisionName} data...</div>
      </div>
    );
  }

  if (payoutError) {
    return (
      <Alert variant="danger" className="mt-3">
        <Alert.Heading>Error Loading Data</Alert.Heading>
        <p>Unable to load data for {divisionName}. Please try again.</p>
        <small className="text-muted">Error: {payoutError?.message}</small>
      </Alert>
    );
  }

  if (!players || players.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <p>No players found for {divisionName}</p>
      </div>
    );
  }

  // Calculate payout summary using the shared utility
  const payoutSummary = calculatePayoutSummary(
    allPayouts,
    paytableData,
    prizePool,
    placesPaid
  );

  // Calculate the actual sum of payouts being displayed in the table with reconciliation
  const calculateDisplayedPayoutsTotal = () => {
    // First, get all the payout amounts as they would be displayed
    const payoutAmounts = [];
    playersWithRanks.forEach((player) => {
      const payoutInfo = getPayoutInfo(player.rank);
      if (payoutInfo.status === "Yes") {
        payoutAmounts.push(payoutInfo.amount);
      }
    });

    if (payoutAmounts.length === 0) {
      return 0;
    }

    // Calculate the sum
    const totalCalculatedPayouts = payoutAmounts.reduce(
      (sum, amount) => sum + amount,
      0
    );
    const difference = prizePool - totalCalculatedPayouts;

    // If there's any difference, apply reconciliation like the backend does
    if (Math.abs(difference) > 0.000001) {
      // Find the largest payout to adjust
      const largestPayoutIndex = payoutAmounts.reduce(
        (maxIndex, amount, index) => {
          return amount > payoutAmounts[maxIndex] ? index : maxIndex;
        },
        0
      );

      // Adjust the largest payout and round to exactly 2 decimal places
      const adjustedAmount = payoutAmounts[largestPayoutIndex] + difference;
      payoutAmounts[largestPayoutIndex] =
        Math.round(adjustedAmount * 100) / 100;
    }

    return payoutAmounts.reduce((sum, amount) => sum + amount, 0);
  };

  // Get reconciled payout amounts for display in the table
  const getReconciledPayoutAmounts = () => {
    // Get all players who will receive payouts
    const payoutPlayers = playersWithRanks.filter((player) => {
      const payoutInfo = getPayoutInfo(player.rank);
      return payoutInfo.status === "Yes";
    });

    if (payoutPlayers.length === 0) {
      return {};
    }

    // Calculate initial payout amounts
    const payoutAmounts = payoutPlayers.map((player) => {
      const payoutInfo = getPayoutInfo(player.rank);
      return {
        playerId: player.id,
        amount: payoutInfo.amount,
        percentage: payoutInfo.percentage,
      };
    });

    // Calculate the sum and apply reconciliation if needed
    const totalCalculatedPayouts = payoutAmounts.reduce(
      (sum, p) => sum + p.amount,
      0
    );
    const difference = prizePool - totalCalculatedPayouts;

    if (Math.abs(difference) > 0.000001) {
      // Find the largest payout to adjust
      const largestPayoutIndex = payoutAmounts.reduce(
        (maxIndex, payout, index) => {
          return payout.amount > payoutAmounts[maxIndex].amount
            ? index
            : maxIndex;
        },
        0
      );

      // Adjust the largest payout
      const adjustedAmount =
        payoutAmounts[largestPayoutIndex].amount + difference;
      payoutAmounts[largestPayoutIndex].amount =
        Math.round(adjustedAmount * 100) / 100;
    }

    // Return a map of player ID to reconciled amount
    const reconciledAmounts = {};
    payoutAmounts.forEach((payout) => {
      reconciledAmounts[payout.playerId] = {
        amount: payout.amount,
        percentage: payout.percentage,
      };
    });

    return reconciledAmounts;
  };

  const displayedPayoutsTotal = calculateDisplayedPayoutsTotal();
  const reconciledPayouts = getReconciledPayoutAmounts();

  return (
    <div className="mt-3" style={{ transition: "all 0.2s ease-in-out" }}>
      {/* Division Summary */}
      <div
        className="row g-2 mb-4"
        style={{
          transition: "all 0.3s ease-in-out",
          opacity: payoutStatus === "loading" ? 0.7 : 1,
        }}
      >
        <div className="col-6 col-sm-4 col-lg-2">
          <div className="text-center p-2 bg-light rounded h-100">
            <div
              className="h6 mb-1 text-primary"
              style={{ whiteSpace: "nowrap" }}
            >
              {players.length}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Total Players
            </small>
          </div>
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <div className="text-center p-2 bg-light rounded h-100">
            <div
              className="h6 mb-1 text-warning"
              style={{ whiteSpace: "nowrap" }}
            >
              {placesPaid}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Places Paid ({payoutPercentage}%)
            </small>
          </div>
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <div className="text-center p-2 bg-light rounded h-100">
            <div className="h6 mb-1 text-info" style={{ whiteSpace: "nowrap" }}>
              ${(divisionData.division_cost || 0).toFixed(2)}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Entry Cost
            </small>
          </div>
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <div className="text-center p-2 bg-light rounded h-100">
            <div
              className="h6 mb-1 text-danger"
              style={{ whiteSpace: "nowrap" }}
            >
              $
              {(
                (divisionData.division_rake_per_player || 0) * players.length
              ).toFixed(2)}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Fees Collected
            </small>
          </div>
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <div className="text-center p-2 bg-light rounded h-100">
            <div
              className="h6 mb-1 text-success"
              style={{ whiteSpace: "nowrap" }}
            >
              $
              {(
                ((divisionData.division_cost || 0) -
                  (divisionData.division_rake_per_player || 0)) *
                players.length
              ).toFixed(2)}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Prize Pool
            </small>
          </div>
        </div>
        <div className="col-6 col-sm-4 col-lg-2">
          <div className="text-center p-2 bg-light rounded h-100">
            <div
              className={`h6 mb-1 ${
                Math.abs(displayedPayoutsTotal - prizePool) < 0.01
                  ? "text-success"
                  : displayedPayoutsTotal > prizePool
                  ? "text-danger"
                  : "text-warning"
              }`}
              style={{ whiteSpace: "nowrap" }}
            >
              ${displayedPayoutsTotal.toFixed(2)}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Calculated Payouts
              {Math.abs(displayedPayoutsTotal - prizePool) >= 0.01 && (
                <span
                  className={`d-block mt-1 ${
                    displayedPayoutsTotal > prizePool
                      ? "text-danger"
                      : "text-warning"
                  }`}
                  style={{ fontSize: "0.7rem" }}
                >
                  ⚠️ Check needed
                </span>
              )}
            </small>
          </div>
        </div>
      </div>

      {/* Division Configuration Controls */}
      <div className="mb-4">
        <h6
          className="mb-3 text-muted"
          style={{ fontSize: "0.875rem", fontWeight: "600" }}
        >
          📊 Payout Configuration
          {payoutStatus === "loading" && (
            <Spinner
              animation="border"
              size="sm"
              className="ms-2"
              style={{ width: "1rem", height: "1rem" }}
            />
          )}
        </h6>
        <Row>
          <Col xs={12} sm={6} lg={4}>
            <Form.Group className="mb-3 mb-sm-0">
              <Form.Label className="small text-muted fw-semibold mb-2">
                💰 Percentage of Places Paid
              </Form.Label>
              <Form.Control
                as="select"
                style={{
                  color: "#2D083F",
                  height: "38px",
                  lineHeight: "1.5",
                  padding: "6px 12px",
                  fontSize: "1rem",
                  transition: "all 0.2s ease-in-out",
                }}
                value={payoutPercentage}
                onChange={(e) =>
                  onUpdateSettings &&
                  onUpdateSettings({
                    payoutPercentage: parseInt(e.target.value),
                  })
                }
                disabled={payoutStatus === "loading"}
              >
                <option value={30}>30% of Players</option>
                <option value={40}>40% of Players</option>
                <option value={45}>45% of Players</option>
                <option value={50}>50% of Players</option>
              </Form.Control>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} lg={4}>
            <Form.Group className="mb-3 mb-sm-0">
              <Form.Label className="small text-muted fw-semibold mb-2">
                🏆 Payout Curve
              </Form.Label>
              <Form.Control
                as="select"
                style={{
                  color: "#2D083F",
                  height: "38px",
                  lineHeight: "1.5",
                  padding: "6px 12px",
                  fontSize: "1rem",
                  transition: "all 0.2s ease-in-out",
                }}
                value={selectedPaytableId}
                onChange={(e) =>
                  onUpdateSettings &&
                  onUpdateSettings({ selectedPaytableId: e.target.value })
                }
                disabled={
                  payoutStatus === "loading" || paytablesStatus === "loading"
                }
              >
                {availablePaytables?.map((paytable) => (
                  <option
                    key={paytable.paytable_id}
                    value={paytable.paytable_id}
                  >
                    {paytable.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* Players Table */}
      <div
        className="table-responsive"
        style={{
          transition: "all 0.3s ease-in-out",
          opacity: payoutStatus === "loading" ? 0.7 : 1,
        }}
      >
        {/* Payout Validation */}
        {!payoutSummary.isValid && (
          <div className="alert alert-warning mb-3">
            <h6>⚠️ Payout Validation Warning</h6>
            <small>
              Total payout percentage:{" "}
              {(payoutSummary.totalPercentage * 100).toFixed(2)}% (should be
              100.00%)
              <br />
              Total payout amount: ${payoutSummary.totalAmount.toFixed(2)}
              (prize pool: ${prizePool.toFixed(2)})
              {payoutSummary.difference > 0.01 && (
                <>
                  <br />
                  <strong>
                    Difference: ${payoutSummary.difference.toFixed(2)}
                  </strong>
                </>
              )}
            </small>
          </div>
        )}

        <Table striped bordered hover responsive>
          <thead style={{ background: "#2D083F", color: "white" }}>
            <tr>
              <th className="text-center">Rank</th>
              <th className="text-center">Player</th>
              <th className="text-center">Score</th>
              {throwScoringModeId > 1 && (
                <th className="text-center">Score+</th>
              )}
              <th className="text-center">Payout</th>
            </tr>
          </thead>
          <tbody>
            {playersWithRanks.map((player) => {
              const payoutInfo = getPayoutInfo(player.rank);
              const isReceivingPayout = payoutInfo.status === "Yes";

              return (
                <tr
                  key={player.id}
                  style={{
                    opacity: isReceivingPayout ? 1 : 0.6,
                    backgroundColor: isReceivingPayout ? "inherit" : "#f8f9fa",
                  }}
                >
                  <td className="text-center">
                    <Badge
                      bg={
                        typeof player.rank === "number" && player.rank <= 3
                          ? "warning"
                          : "secondary"
                      }
                      className="px-2"
                      style={{ opacity: isReceivingPayout ? 1 : 0.7 }}
                    >
                      {player.rank}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <Link
                      to={`/player?id=${player.user_id}`}
                      style={{
                        color: isReceivingPayout ? "#2D083F" : "#6c757d",
                        textDecoration: "none",
                        opacity: isReceivingPayout ? 1 : 0.8,
                      }}
                    >
                      {player.users_public.name}
                    </Link>
                  </td>
                  <td className="text-center">
                    {player.is_dnf ? (
                      <Badge
                        bg="danger"
                        style={{ opacity: isReceivingPayout ? 1 : 0.7 }}
                      >
                        DNF
                      </Badge>
                    ) : player.is_dropped ? (
                      <s className="text-muted">{player.score}</s>
                    ) : (
                      <strong style={{ opacity: isReceivingPayout ? 1 : 0.7 }}>
                        {player.score || "—"}
                      </strong>
                    )}
                    {player.score !== null &&
                      !player.is_dnf &&
                      !player.is_dropped && (
                        <div className="small">
                          <Link
                            to={`/display_scorecard?evententry=${player.id}&event=${eventId}`}
                            className="text-decoration-none"
                            style={{ opacity: isReceivingPayout ? 1 : 0.6 }}
                          >
                            📇
                          </Link>
                        </div>
                      )}
                  </td>
                  {throwScoringModeId > 1 && (
                    <td className="text-center">
                      {player.is_dnf ? (
                        <Badge
                          bg="danger"
                          style={{ opacity: isReceivingPayout ? 1 : 0.7 }}
                        >
                          DNF
                        </Badge>
                      ) : (
                        <strong
                          style={{ opacity: isReceivingPayout ? 1 : 0.7 }}
                        >
                          {player.score_adv || "—"}
                        </strong>
                      )}
                    </td>
                  )}
                  <td className="text-center">
                    {(() => {
                      if (isReceivingPayout) {
                        // Use reconciled amount if available, otherwise fall back to calculated amount
                        const reconciledPayout = reconciledPayouts[player.id];
                        const displayAmount = reconciledPayout
                          ? reconciledPayout.amount
                          : payoutInfo.amount;
                        const displayPercentage = reconciledPayout
                          ? reconciledPayout.percentage
                          : payoutInfo.percentage;

                        return (
                          <div>
                            <Badge bg="success" className="px-2 mb-1">
                              ${displayAmount.toFixed(2)}
                            </Badge>
                            <br />
                            <small className="text-muted">
                              {(displayPercentage * 100).toFixed(1)}%
                            </small>
                          </div>
                        );
                      } else {
                        return <span className="text-muted">—</span>;
                      }
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export default DivisionPayoutTab;
